import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client';
import type { Envelope } from '../../api/contract';
import { useAccountApi } from '../../app/AppContext';
import { Dialog } from '../../shared/ui/Dialog';
import { createAuthRequestKey, isTerminalReauthFailure, reauthFailureText, startLinkReauth, type Identity } from './api';
import { clearReauthIntent, hasFreshReauth, readReauthIntent } from './reauthReturn';
import './auth.css';

export function SignInMethodsPage() {
  const api = useAccountApi();
  const navigate = useNavigate();
  const [identities, setIdentities] = useState<Identity[]>();
  const [enabled, setEnabled] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [pending, setPending] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [retryAt, setRetryAt] = useState(0);
  const [fresh, setFresh] = useState(() => hasFreshReauth(api.epoch));
  const input = useRef<HTMLInputElement>(null);
  const busy = useRef(false);
  const active = useRef(false);
  const challengeKey = useRef<string | undefined>(undefined);
  const attempt = useRef<{ email: string; key: string } | undefined>(undefined);
  useEffect(() => {
    active.current = true;
    document.title = 'Способы входа — Казна';
    void Promise.all([
      api.apiGet<Envelope<Identity[]>>('/me/identities'),
      api.apiGet<Envelope<{ emailAuth: { enabled: boolean } }>>('/capabilities'),
    ]).then(([methods, capabilities]) => {
      if (active.current) { setIdentities(methods.data); setEnabled(capabilities.data.emailAuth.enabled); }
    }).catch(caught => { if (active.current) setError(caught instanceof Error ? caught.message : 'Не удалось загрузить способы входа.'); });
    return () => { active.current = false; };
  }, [api]);
  useEffect(() => {
    if (!retryAt) return;
    const timer = window.setTimeout(() => setRetryAt(0), Math.min(Math.max(0, retryAt - Date.now()), 2_147_483_647));
    return () => window.clearTimeout(timer);
  }, [retryAt]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy.current || retryAt > Date.now()) return;
    if (fresh && !hasFreshReauth(api.epoch)) {
      setFresh(false); setError('Подтверждение истекло. Подтвердите вход через Яндекс ID и введите адрес заново.'); return;
    }
    if (hasFreshReauth(api.epoch) && !email) { setInvalid(true); setError('Введите адрес электронной почты.'); input.current?.focus(); return; }
    busy.current = true; setPending(true); setError('');
    const intent = readReauthIntent();
    const ownedIntent = intent?.epoch === api.epoch && intent.actionId === undefined ? intent : undefined;
    try {
      if (!hasFreshReauth(api.epoch)) {
        challengeKey.current ??= createAuthRequestKey();
        const url = await startLinkReauth(api, challengeKey.current);
        api.assertCurrent();
        if (active.current) window.location.assign(url);
        return;
      }
      if (attempt.current?.email !== email) attempt.current = { email, key: createAuthRequestKey() };
      await api.authMutation('/me/identities/email/link-requests', { email }, attempt.current.key);
      api.assertCurrent(); clearReauthIntent(ownedIntent);
      if (active.current) { setAccepted(true); setEmail(''); attempt.current = undefined; }
    } catch (caught) {
      // Protected failure can already have unmounted this page.
      if (isTerminalReauthFailure(caught)) clearReauthIntent(ownedIntent);
      if (active.current) {
        setError(reauthFailureText(caught));
        if (caught instanceof ApiError && caught.retryAfterMs !== undefined) setRetryAt(Date.now() + caught.retryAfterMs);
      }
    } finally { busy.current = false; if (active.current) setPending(false); }
  };
  const hasYandex = identities?.some(identity => identity.provider === 'YANDEX');
  const hasEmail = identities?.some(identity => identity.provider === 'EMAIL');
  return <Dialog title="Способы входа" onClose={() => navigate('/account')}>
    <section className="card form auth-methods" aria-busy={pending}>
      {!identities && !error && <p role="status">Загружаем способы входа…</p>}
      {identities?.filter(identity => identity.provider !== 'TELEGRAM').map(identity => <div key={identity.provider}><h3>{identity.provider === 'YANDEX' ? 'Яндекс ID' : 'Электронная почта'}</h3><p>{identity.provider === 'EMAIL' ? identity.email : 'Подключён'}</p></div>)}
      {identities?.length === 0 && <p>Подтверждённых способов входа нет.</p>}
      {identities && !enabled && <p>Вход по почте временно недоступен. Яндекс ID продолжает работать.</p>}
      {error && <p id="methods-error" className="error" role="alert">{error}</p>}
      {accepted ? <p role="status">Если адрес доступен для привязки, письмо уже отправлено. Если он связан с другим аккаунтом, войдите в тот аккаунт отдельно. Аккаунты не объединяются.</p> : enabled && hasYandex && !hasEmail && <form className="form" noValidate onSubmit={event => void submit(event)}>
        <p>Добавьте почту и пароль к этому аккаунту. Сначала подтвердите вход через Яндекс ID.</p>
        {fresh && <label className="field" htmlFor="link-email"><span>Электронная почта</span><input ref={input} id="link-email" type="text" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} value={email} readOnly={pending} required aria-invalid={invalid} aria-describedby={error ? 'methods-error' : undefined} onChange={event => { setEmail(event.target.value); setInvalid(false); attempt.current = undefined; }} /></label>}
        <button className="button button-primary wide" type="submit" disabled={pending || retryAt > 0}>{pending ? 'Продолжаем…' : fresh ? 'Отправить письмо' : 'Подтвердить через Яндекс ID'}</button>
        {retryAt > 0 && <p role="status">Дождитесь окончания паузы перед повторной попыткой.</p>}
      </form>}
    </section>
  </Dialog>;
}
